/* Singleton CourseProgress contains all progress information for a given course
 *
 * CourseProgress's attributes are exactly the attributes returned by the
 * course progress API. */
import Backbone from 'backbone-associations';
import _ from 'underscore';

import multitracker from 'js/app/multitrackerSingleton';

import constants from 'pages/open-course/common/constants';
import { memoizedCourseProgressPromise } from 'pages/open-course/common/data/courseProgress';
import Progress from 'pages/open-course/common/models/progress';

const CourseProgress = Backbone.AssociatedModel.extend({
  resourceName: 'onDemandCoursesProgress.v1',

  defaults: {
    items: {},
    lessons: {},
  },

  initialize() {
    this.progressMapping = {};
    this.completedLessons = this.calculateCompletedLessons();
    this.completedModules = this.calculateCompletedModules();
    this.on('refresh', this.refresh, this);
  },

  hasStarted() {
    return this.get('overall') > 0;
  },

  checkLessonCompletion() {
    const updateCompletedLessons = this.calculateCompletedLessons();
    const newCompletedLessons = _.difference(updateCompletedLessons, this.completedLessons);

    if (newCompletedLessons.length !== 0) {
      this.completedLessons = updateCompletedLessons;
      multitracker.pushV2([
        'open_course.lesson.complete',
        {
          lesson_id: newCompletedLessons[0],
          open_course_slug: constants.courseSlug,
        },
      ]);
    }
  },

  checkModuleCompletion() {
    const updateCompletedModules = this.calculateCompletedModules();
    const newCompletedModules = _.difference(updateCompletedModules, this.completedModules);

    if (newCompletedModules.length !== 0) {
      this.completedModules = updateCompletedModules;
      multitracker.pushV2([
        'open_course.module.complete',
        {
          module_id: newCompletedModules[0],
          open_course_slug: constants.courseSlug,
        },
      ]);
    }
  },

  _calculateCompleted(itemType: $TSFixMe) {
    return _(this.get(itemType))
      .chain()
      .pairs()
      .filter(function (item) {
        return item[1] === 100;
      })
      .map(function (item) {
        return item[0];
      })
      .value();
  },

  calculateCompletedLessons() {
    return this._calculateCompleted('lessons');
  },

  calculateCompletedModules() {
    return this._calculateCompleted('modules');
  },

  refresh() {
    const promise = memoizedCourseProgressPromise.force(constants.courseSlug).then(
      function (newCourseProgress: $TSFixMe) {
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        this.set(newCourseProgress);
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        this.updateItemProgresses();
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        this.checkLessonCompletion();
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        this.checkModuleCompletion();
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        this.trigger('refreshed');
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        return this;
      }.bind(this)
    );
    promise.done();
    return promise;
  },

  hasItem(id: $TSFixMe) {
    return _(this.get('items')).has(id);
  },

  getItem(id: $TSFixMe) {
    return this.get('items')[id];
  },

  /**
   * Get the attributes for `itemId`'s progress. Returns a new copy of the
   * attributes every time you call it, and the returned attributes do not
   * change when the item's progress changes.
   */
  getItemProgressAttributes(itemId: $TSFixMe) {
    const progressAttributes = {
      id: itemId,
      timestamp: Date.now(),
      progressState: constants.progressNotStarted,
    };
    if (this.hasItem(itemId)) {
      _(progressAttributes).extend(this.getItem(itemId));
    }
    return progressAttributes;
  },

  /**
   * Get an `ItemProgress` model for `itemId`. You are guaranteed to get the
   * same model every time you call this, and that model is guaranteed to
   * change whenever the item's progress changes.
   */
  getItemProgress(itemId: $TSFixMe) {
    if (!this.progressMapping[itemId]) {
      const progressAttributes = this.getItemProgressAttributes(itemId);
      const progress = new Progress(progressAttributes);
      this.listenTo(
        progress,
        'change:progressState',
        function (progressModel: $TSFixMe, newState: $TSFixMe, options: $TSFixMe) {
          if (options.refreshCourseProgress) {
            // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
            this.trigger('refresh');
          }
        }.bind(this)
      );
      this.progressMapping[itemId] = progress;
    }

    return this.progressMapping[itemId];
  },

  getGradedAssignmentGroupProgress(groupId: $TSFixMe) {
    const gradedAssignmentGroupProgresses = this.get('gradedAssignmentGroupProgress');
    return gradedAssignmentGroupProgresses[groupId] && gradedAssignmentGroupProgresses[groupId].state;
  },

  /**
   * Call this if `this.get('items')` has changed and you want all the
   * `ItemProgress`es returned by `getItemProgress` to change to correspond.
   */
  updateItemProgresses() {
    _(this.progressMapping).each(
      function (itemProgress: $TSFixMe, itemId: $TSFixMe) {
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        const attrs = this.getItemProgressAttributes(itemId);
        itemProgress.setState(attrs.progressState, {
          refreshCourseProgress: false,
        });

        // Update all of the content of a progress item.
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        if (this.hasItem(itemId)) {
          // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
          itemProgress.set(this.getItem(itemId));
        }
      }.bind(this)
    );
  },

  hasModule(id: $TSFixMe) {
    return _(this.get('modules')).has(id);
  },

  // Used only in ProgressStore.ts in getLessonProgress
  getProgressForLesson(lessonId: $TSFixMe) {
    const completionPercent = this.get('lessons')[lessonId];
    let progressState;

    if (completionPercent === 100) {
      progressState = 'Completed';
    } else if (completionPercent > 0) {
      progressState = 'Started';
    } else {
      progressState = 'NotStarted';
    }

    return {
      completionPercent,
      progressState,
    };
  },

  getItemProgressesForLesson(lesson: $TSFixMe) {
    const progresses = lesson.getItemMetadatas().map((item: $TSFixMe) => {
      const progress = this.getItemProgress(item.get('id'));
      return {
        item,
        progress,
      };
    });

    return progresses;
  },
});

/** mock of the progress object for no progress */
const nullOverrides = {
  // if there is no courseProgress, nothing is started.
  hasNotStarted() {
    return true;
  },
  getItemProgress(id: $TSFixMe) {
    return new Progress({
      id,
      timestamp: Date.now(),
      progressState: constants.progressNotStarted,
    });
  },
};

CourseProgress.nullProgressSingleton = Object.freeze(_({}).extend(new CourseProgress(), nullOverrides));

export default CourseProgress;
